import { useState, useCallback, useRef, useEffect } from "react";
import api from "../api/client";

const TYPES = ["Supplier","Client","Networking","Personal","Other"];
// Shown until the company's own list has loaded (the server adds the categories a company created itself)
const DEFAULT_CATS = ["IT","Cybersecurity","Telecom","Construction","Healthcare","Manufacturing","Other"];

// Subcategory is hidden on every screen; the column and the values already saved are kept, nothing new is entered.
const blank = {
  name:"", designation:"", company:"", email:"", mobile:"",
  telephone:"", website:"", address:"",
  contact_type:"Other", category:"IT",
};

// One shared category list, so a category added in the Edit form also shows in the Add form and the filter
const catStore = { list: null, loading: false, subs: new Set() };
const publishCats = (list) => { catStore.list = list; catStore.subs.forEach(fn => fn(list)); };
function useCategories() {
  const [cats, setCats] = useState(catStore.list || DEFAULT_CATS);
  useEffect(() => {
    catStore.subs.add(setCats);
    if (!catStore.list && !catStore.loading) {
      catStore.loading = true;
      api.get("/bizcard/categories").then(r => publishCats(r.data.categories)).catch(() => {}).finally(() => { catStore.loading = false; });
    }
    return () => { catStore.subs.delete(setCats); };
  }, []);
  return cats;
}

/* The Category drop-down: the company's list plus "+ Add new category…", which opens a small box to type the name. */
function CategorySelect({ value, onChange }) {
  const cats = useCategories();
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const options = value && !cats.some(c => c.toLowerCase() === value.toLowerCase()) ? [value, ...cats] : cats;
  const close = () => { setAdding(false); setName(""); setErr(""); };

  const add = async () => {
    const n = name.trim().replace(/\s+/g, " ");
    if (n.length < 2) { setErr("Type at least 2 letters."); return; }
    const same = cats.find(c => c.toLowerCase() === n.toLowerCase());
    if (same) { onChange(same); close(); return; }          // it already exists: just pick it
    setBusy(true); setErr("");
    try {
      const r = await api.post("/bizcard/categories", { name: n });
      publishCats(r.data.categories);
      onChange(r.data.name); close();
    } catch (e) {
      const d = e.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Could not add the category.");
    } finally { setBusy(false); }
  };

  return (
    <>
      <select className="bc-select" value={value || cats[0]} onChange={e => e.target.value === "__add__" ? setAdding(true) : onChange(e.target.value)}>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
        <option value="__add__">+ Add new category…</option>
      </select>
      {adding && (
        <div style={{display:"flex",gap:8,flexWrap:"wrap",marginTop:6}}>
          <input className="bc-input" style={{flex:1,minWidth:140}} autoFocus maxLength={40} placeholder="New category name" value={name}
                 onChange={e => setName(e.target.value)} onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); add(); } }} />
          <button type="button" className="bc-btn bc-btn-primary" style={{fontSize:12,padding:"7px 14px"}} onClick={add} disabled={busy}>{busy ? "Adding…" : "Add"}</button>
          <button type="button" className="bc-btn bc-btn-secondary" style={{fontSize:12,padding:"7px 14px"}} onClick={close} disabled={busy}>Cancel</button>
          {err && <div className="bc-alert bc-alert-error" style={{width:"100%"}}>{err}</div>}
        </div>
      )}
    </>
  );
}

/* ── Shared CSS ────────────────────────────────────────────── */
const CSS = `
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
:root {
  --bg:       #f0f4fc;
  --surface:  #ffffff;
  --s2:       #f4f8ff;
  --border:   #c0d0eb;
  --b2:       #d5e3f5;
  --accent:   #2563eb;
  --text:     #0d1a36;
  --text2:    #2a4a78;
  --muted:    #6080aa;
  --green:    #059669;
  --red:      #dc2626;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  color-scheme: light;
}
body { background: var(--bg); color: var(--text); }

/* Tabs */
.bc-tabs { display: flex; border-bottom: 1px solid var(--b2); margin-bottom: 28px; gap: 0; }
.bc-tab {
  padding: 0 20px; height: 40px; font-size: 12px; font-weight: 500; color: var(--muted);
  background: none; border: none; border-bottom: 2px solid transparent;
  margin-bottom: -1px; cursor: pointer; white-space: nowrap;
  transition: color .15s, border-color .15s; letter-spacing: .01em;
}
.bc-tab:hover { color: var(--text2); }
.bc-tab.active { color: var(--accent); border-bottom-color: var(--accent); font-weight: 600; }

/* Section title */
.bc-section-title {
  font-size: 11px; font-weight: 700; letter-spacing: .08em; text-transform: uppercase;
  color: var(--muted); margin-bottom: 20px;
}

/* Drop zone */
.bc-dropzone {
  border: 2px dashed var(--border); border-radius: 10px; padding: 36px 24px;
  text-align: center; cursor: pointer; transition: border-color .2s, background .2s;
  background: var(--surface); margin-bottom: 20px;
}
.bc-dropzone.drag, .bc-dropzone:hover { border-color: var(--accent); background: #f4f8ff; }
.bc-dropzone-icon { font-size: 32px; margin-bottom: 8px; }
.bc-dropzone-label { font-size: 13px; color: var(--text2); }
.bc-dropzone-label span { color: var(--accent); font-weight: 500; }
.bc-dropzone-file { font-size: 12px; color: var(--green); margin-top: 6px; font-weight: 500; }
.bc-dropzone-label-row { font-size: 12px; font-weight: 600; color: var(--text2); margin-bottom: 6px; }
.bc-dropzone-pair { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.bc-dropzone-col { display: flex; flex-direction: column; }
.bc-dropzone-col-label { font-size: 12px; font-weight: 600; color: var(--text2); margin-bottom: 6px; display: flex; align-items: center; gap: 6px; }
.bc-dropzone-opt { font-size: 10px; font-weight: 500; color: var(--text2); text-transform: uppercase; letter-spacing: .04em; }
.bc-dropzone-compact { padding: 20px 14px; margin-bottom: 8px; }
.bc-dropzone-compact .bc-dropzone-icon { font-size: 24px; margin-bottom: 6px; }
@media (max-width: 560px) { .bc-dropzone-pair { grid-template-columns: 1fr; } }

/* Buttons */
.bc-btn {
  display: inline-flex; align-items: center; gap: 7px;
  padding: 9px 18px; border-radius: 7px; border: none; cursor: pointer;
  font-size: 13px; font-weight: 600; letter-spacing: .01em;
  transition: opacity .15s, box-shadow .15s;
}
.bc-btn:disabled { opacity: .4; cursor: not-allowed; }
.bc-btn:not(:disabled):hover { opacity: .88; }
.bc-btn-primary { background: var(--accent); color: #fff; }
.bc-btn-secondary { background: var(--surface); border: 1px solid var(--border); color: var(--text2); }
.bc-btn-success { background: var(--green); color: #f0fdf4; }
.bc-btn-danger { background: none; border: 1px solid rgba(248,113,113,.3); color: var(--red); }
.bc-actions { display: flex; gap: 10px; flex-wrap: wrap; margin-top: 24px; align-items: center; }

/* Form grid */
.bc-card { background: var(--surface); border: 1px solid var(--border); border-radius: 10px; padding: 24px; margin-bottom: 16px; }
.bc-card-title { font-size: 12px; font-weight: 700; letter-spacing: .07em; text-transform: uppercase; color: var(--muted); margin-bottom: 20px; }
.bc-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
.bc-grid-3 { grid-template-columns: 1fr 1fr 1fr; }
.bc-grid-full { grid-column: 1 / -1; }

.bc-field { display: flex; flex-direction: column; gap: 5px; }
.bc-label { font-size: 11px; font-weight: 600; letter-spacing: .06em; text-transform: uppercase; color: var(--muted); }
.bc-input, .bc-select, .bc-textarea {
  background: var(--s2); border: 1px solid var(--b2); border-radius: 7px;
  padding: 9px 12px; font-size: 13px; color: var(--text);
  outline: none; width: 100%; transition: border-color .15s;
  font-family: inherit;
}
.bc-input:focus, .bc-select:focus, .bc-textarea:focus { border-color: var(--accent); }
.bc-textarea { resize: vertical; min-height: 72px; }
.bc-select { appearance: none; cursor: pointer;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%234a6388' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
  background-repeat: no-repeat; background-position: right 10px center; padding-right: 30px; }

/* Intel box */
.bc-intel { background: #f8faff; border: 1px solid var(--border); border-radius: 10px; padding: 20px; margin-bottom: 16px; }
.bc-intel-title { font-size: 11px; font-weight: 700; letter-spacing: .07em; text-transform: uppercase; color: var(--accent); margin-bottom: 10px; }
.bc-intel-text { font-size: 13px; color: var(--text2); line-height: 1.65; margin-bottom: 12px; }
.bc-tags { display: flex; flex-wrap: wrap; gap: 6px; }
.bc-tag { padding: 3px 9px; border-radius: 20px; font-size: 11px; font-weight: 500; background: rgba(64,119,244,.12); color: var(--accent); border: 1px solid rgba(64,119,244,.2); }

/* Alert */
.bc-alert {
  padding: 11px 16px; border-radius: 8px; font-size: 13px; margin-bottom: 20px;
  display: flex; align-items: center; gap: 10px;
}
.bc-alert-success { background: rgba(16,185,129,.08); border: 1px solid rgba(16,185,129,.2); color: var(--green); }
.bc-alert-error   { background: rgba(248,113,113,.08); border: 1px solid rgba(248,113,113,.2); color: var(--red); }

/* Contacts list */
.bc-contact-card {
  background: var(--surface); border: 1px solid var(--border); border-radius: 10px;
  margin-bottom: 10px; overflow: hidden;
  transition: border-color .15s;
}
.bc-contact-card:hover { border-color: #dbeafe; }
.bc-contact-header {
  display: flex; align-items: center; padding: 14px 18px; gap: 12px; cursor: pointer;
}
.bc-contact-avatar {
  width: 38px; height: 38px; border-radius: 50%; flex-shrink: 0;
  background: linear-gradient(135deg, var(--accent), #7c3aed);
  display: flex; align-items: center; justify-content: center;
  font-size: 14px; font-weight: 700; color: #fff;
}
.bc-contact-info { flex: 1; min-width: 0; }
.bc-contact-name { font-size: 14px; font-weight: 600; letter-spacing: -.01em; }
.bc-contact-meta { font-size: 12px; color: var(--text2); margin-top: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.bc-contact-uploader { font-size: 11px; color: var(--muted); margin-top: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.bc-contact-badges { display: flex; gap: 6px; align-items: center; flex-shrink: 0; }
.bc-badge { padding: 2px 8px; border-radius: 20px; font-size: 10px; font-weight: 600; letter-spacing: .04em; text-transform: uppercase; background: var(--s2); border: 1px solid var(--b2); color: var(--text2); }
.bc-badge-blue { background: rgba(64,119,244,.1); border-color: rgba(64,119,244,.2); color: var(--accent); }
.bc-chevron { font-size: 12px; color: var(--muted); flex-shrink: 0; transition: transform .2s; }
.bc-chevron.open { transform: rotate(180deg); }

.bc-contact-body { padding: 0 18px 18px; border-top: 1px solid var(--b2); }
.bc-detail-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; padding-top: 16px; }
.bc-detail-item { }
.bc-detail-label { font-size: 10px; font-weight: 600; letter-spacing: .07em; text-transform: uppercase; color: var(--muted); margin-bottom: 3px; }
.bc-detail-val { font-size: 13px; color: var(--text2); }
.bc-intel-section { margin-top: 14px; padding-top: 14px; border-top: 1px solid var(--b2); }

.bc-contact-actions { display: flex; gap: 8px; margin-top: 16px; }

/* Search mode toggle */
.bc-mode-row { display: flex; gap: 8px; margin-bottom: 24px; }
.bc-mode-btn {
  padding: 7px 16px; border-radius: 7px; font-size: 12px; font-weight: 600;
  cursor: pointer; border: 1px solid var(--border); background: var(--surface); color: var(--muted);
  transition: all .15s;
}
.bc-mode-btn.active { background: var(--accent); border-color: var(--accent); color: #fff; }

/* Filters row */
.bc-filter-row { display: flex; gap: 10px; flex-wrap: wrap; margin-bottom: 20px; align-items: flex-end; }
.bc-filter-row .bc-field { flex: 1; min-width: 130px; }
.bc-pagination { display: flex; align-items: center; justify-content: center; gap: 16px; margin-top: 20px; }

/* Search bar */
.bc-search-bar { display: flex; gap: 10px; margin-bottom: 8px; }
.bc-search-input-wrap { position: relative; flex: 1; }
.bc-search-icon { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: var(--muted); font-size: 14px; pointer-events: none; }
.bc-search-bar .bc-input { padding-left: 36px; }

/* Empty */
.bc-empty { text-align: center; padding: 60px 20px; color: var(--muted); font-size: 13px; }
.bc-empty-icon { font-size: 40px; margin-bottom: 12px; opacity: .5; }

/* Spinner */
.bc-spinner { display: inline-block; width: 14px; height: 14px; border: 2px solid rgba(255,255,255,.2); border-top-color: #fff; border-radius: 50%; animation: spin .6s linear infinite; flex-shrink: 0; }
@keyframes spin { to { transform: rotate(360deg); } }

/* Camera modal */
.bc-cam-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,.85); z-index: 1000;
  display: flex; align-items: center; justify-content: center; padding: 20px;
}
.bc-cam-modal {
  background: var(--surface); border: 1px solid var(--border); border-radius: 14px;
  padding: 24px; width: 100%; max-width: 560px;
  display: flex; flex-direction: column; gap: 16px;
}
.bc-cam-title { font-size: 13px; font-weight: 700; color: var(--text); display: flex; justify-content: space-between; align-items: center; }
.bc-cam-close { background: none; border: none; color: var(--muted); font-size: 20px; cursor: pointer; line-height: 1; }
.bc-cam-close:hover { color: var(--red); }
.bc-cam-video { width: 100%; border-radius: 8px; background: #000; display: block; }
.bc-cam-canvas { display: none; }
.bc-cam-preview { width: 100%; border-radius: 8px; border: 1px solid var(--border); }
.bc-cam-actions { display: flex; gap: 10px; justify-content: flex-end; }

/* Duplicate-contact confirm modal */
.bc-confirm-modal { max-width: 420px; }
.bc-confirm-icon { width: 40px; height: 40px; border-radius: 50%; background: rgba(245,158,11,.12); color: #f59e0b; display: flex; align-items: center; justify-content: center; font-size: 18px; margin-bottom: 4px; }
.bc-confirm-title { font-size: 15px; font-weight: 700; color: var(--text); }
.bc-confirm-body { font-size: 13px; color: var(--text2); line-height: 1.6; }
.bc-confirm-body strong { color: var(--text); }
.bc-confirm-actions { display: flex; gap: 10px; justify-content: flex-end; margin-top: 4px; }

@media (max-width: 640px) { .bc-grid, .bc-detail-grid { grid-template-columns: 1fr; } .bc-grid-3 { grid-template-columns: 1fr; } }
`;

/* ── Camera Modal ─────────────────────────────────────────── */
function CameraModal({ onCapture, onClose }) {
  const videoRef  = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [captured, setCaptured] = useState(null);
  const [error, setError]       = useState("");

  useEffect(() => {
    navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } })
      .then(stream => {
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
      })
      .catch(() => setError("Camera access denied. Please allow camera permission in your browser."));
    return () => streamRef.current?.getTracks().forEach(t => t.stop());
  }, []);

  const snap = () => {
    const v = videoRef.current; const c = canvasRef.current;
    if (!v || !c) return;
    c.width = v.videoWidth; c.height = v.videoHeight;
    c.getContext("2d").drawImage(v, 0, 0);
    c.toBlob(blob => {
      const file = new File([blob], "camera-capture.jpg", { type: "image/jpeg" });
      setCaptured(URL.createObjectURL(blob));
      streamRef.current?.getTracks().forEach(t => t.stop());
      onCapture(file);
    }, "image/jpeg", 0.92);
  };

  const retake = () => {
    setCaptured(null);
    navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } })
      .then(stream => { streamRef.current = stream; if (videoRef.current) videoRef.current.srcObject = stream; });
  };

  return (
    <div className="bc-cam-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bc-cam-modal">
        <div className="bc-cam-title">
          📸 Take a photo of the business card
          <button className="bc-cam-close" onClick={onClose}>×</button>
        </div>
        {error
          ? <div style={{color:'var(--red)',fontSize:13,padding:'20px 0',textAlign:'center'}}>{error}</div>
          : captured
            ? <img src={captured} alt="Captured" className="bc-cam-preview" />
            : <video ref={videoRef} autoPlay playsInline muted className="bc-cam-video" />
        }
        <canvas ref={canvasRef} className="bc-cam-canvas" />
        <div className="bc-cam-actions">
          {captured ? (
            <>
              <button className="bc-btn bc-btn-secondary" onClick={retake}>↩ Retake</button>
              <button className="bc-btn bc-btn-success" onClick={onClose}>✓ Use this photo</button>
            </>
          ) : (
            !error && <button className="bc-btn bc-btn-primary" onClick={snap}>📸 Capture</button>
          )}
          <button className="bc-btn bc-btn-secondary" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

/* ── Duplicate Confirm Modal ──────────────────────────────── */
function DuplicateConfirmModal({ existing, onConfirm, onCancel }) {
  const label = existing.company ? `${existing.name} at ${existing.company}` : existing.name;
  return (
    <div className="bc-cam-overlay" onClick={e => e.target === e.currentTarget && onCancel()}>
      <div className="bc-cam-modal bc-confirm-modal">
        <div className="bc-confirm-icon">⚠</div>
        <div className="bc-confirm-title">Possible duplicate contact</div>
        <div className="bc-confirm-body">
          A contact matching this one already exists — <strong>{label}</strong>{existing.email ? ` (${existing.email})` : ""}.
          Do you still want to save this as a separate contact?
        </div>
        <div className="bc-confirm-actions">
          <button className="bc-btn bc-btn-secondary" onClick={onCancel}>Cancel</button>
          <button className="bc-btn bc-btn-primary" onClick={onConfirm}>Save anyway</button>
        </div>
      </div>
    </div>
  );
}

/* ── Contact Card ─────────────────────────────────────────── */
function ContactCard({ c, onUpdate, onDelete }) {
  const [open, setOpen]   = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm]   = useState({ ...c });
  const [saving, setSaving] = useState(false);
  const f = k => e => setForm(v => ({ ...v, [k]: e.target.value }));
  const initials = (c.name || "?").split(" ").map(w => w[0]).join("").slice(0,2).toUpperCase();

  const [saveError, setSaveError] = useState("");

  const save = async () => {
    setSaving(true); setSaveError("");
    try {
      await api.patch(`/bizcard/contacts/${c.id}`, form);
      onUpdate({ ...c, ...form });
      setEditing(false);
    } catch (e) {
      const detail = e.response?.data?.detail;
      setSaveError(typeof detail === "string" ? detail : "Could not save the contact. Please try again.");
    } finally { setSaving(false); }
  };

  return (
    <div className="bc-contact-card">
      <div className="bc-contact-header" onClick={() => { setOpen(o => !o); setEditing(false); }}>
        <div className="bc-contact-avatar">{initials}</div>
        <div className="bc-contact-info">
          <div className="bc-contact-name">{c.name}</div>
          <div className="bc-contact-meta">{[c.designation, c.company].filter(Boolean).join(" · ")}</div>
          {c.created_by_name && <div className="bc-contact-uploader">Added by {c.created_by_name}</div>}
        </div>
        <div className="bc-contact-badges">
          {c.contact_type && <span className="bc-badge">{c.contact_type}</span>}
          {c.category && <span className="bc-badge bc-badge-blue">{c.category}</span>}
        </div>
        <span className={`bc-chevron${open ? " open" : ""}`}>▼</span>
      </div>

      {open && (
        <div className="bc-contact-body">
          {!editing ? (
            <>
              <div className="bc-detail-grid">
                {[["Email", c.email],["Mobile", c.mobile],["Telephone", c.telephone],
                  ["Website", c.website],["Address", c.address],
                  ["Added by", c.created_by_name]]
                  .filter(([,v]) => v).map(([label, val]) => (
                  <div className="bc-detail-item" key={label}>
                    <div className="bc-detail-label">{label}</div>
                    <div className="bc-detail-val">{val}</div>
                  </div>
                ))}
              </div>
              {c.company_summary && (
                <div className="bc-intel-section">
                  <div className="bc-intel-title" style={{fontSize:10,fontWeight:700,letterSpacing:'.07em',textTransform:'uppercase',color:'var(--accent)',marginBottom:8}}>Company Intelligence</div>
                  <div className="bc-detail-val" style={{marginBottom:10}}>{c.company_summary}</div>
                  {(c.ai_tags||[]).length > 0 && (
                    <div className="bc-tags">{(c.ai_tags||[]).map(t => <span key={t} className="bc-tag">{t}</span>)}</div>
                  )}
                </div>
              )}
              <div className="bc-contact-actions">
                <button className="bc-btn bc-btn-secondary" style={{fontSize:12,padding:'7px 14px'}} onClick={() => setEditing(true)}>Edit</button>
                <button className="bc-btn bc-btn-danger" style={{fontSize:12,padding:'7px 14px'}} onClick={() => onDelete(c.id)}>Delete</button>
              </div>
            </>
          ) : (
            <>
              <div className="bc-grid" style={{paddingTop:16}}>
                {[["name","Name"],["designation","Designation"],["company","Company"],["email","Email"],
                  ["mobile","Mobile"],["telephone","Telephone"],["website","Website"]].map(([k,label]) => (
                  <div className="bc-field" key={k}>
                    <label className="bc-label">{label}</label>
                    <input className="bc-input" value={form[k]||""} onChange={f(k)} />
                  </div>
                ))}
                <div className="bc-field bc-grid-full">
                  <label className="bc-label">Address</label>
                  <textarea className="bc-textarea" value={form.address||""} onChange={f("address")} />
                </div>
              </div>
              <div className="bc-grid" style={{marginTop:14}}>
                <div className="bc-field">
                  <label className="bc-label">Type</label>
                  <select className="bc-select" value={form.contact_type||TYPES[0]} onChange={f("contact_type")}>
                    {TYPES.map(o=><option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div className="bc-field">
                  <label className="bc-label">Category</label>
                  <CategorySelect value={form.category} onChange={v => setForm(x => ({ ...x, category: v }))} />
                </div>
              </div>
              {saveError && <div className="bc-alert bc-alert-error" style={{marginTop:14}}>{saveError}</div>}
              <div className="bc-contact-actions">
                <button className="bc-btn bc-btn-primary" onClick={save} disabled={saving}>
                  {saving && <span className="bc-spinner"/>}{saving?"Saving…":"Save Changes"}
                </button>
                <button className="bc-btn bc-btn-secondary" onClick={()=>{setEditing(false);setForm({...c});setSaveError("");}}>Cancel</button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Main ─────────────────────────────────────────────────── */
export default function BizCard() {
  const [tab, setTab]           = useState(0);
  const [alert, setAlert]       = useState({ msg:"", type:"" });

  // Add Contact
  const [file, setFile]         = useState(null);
  const [backFile, setBackFile] = useState(null);
  const [drag, setDrag]         = useState(false);
  const [backDrag, setBackDrag] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [researching, setResearching] = useState(false);
  const [saving, setSaving]     = useState(false);
  const [form, setForm]         = useState({ ...blank });
  const [intel, setIntel]       = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const [dupConfirm, setDupConfirm] = useState(null);
  const cats = useCategories();

  // Contacts
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [loaded, setLoaded]     = useState(false);
  const [filterType, setFilterType] = useState("");
  const [filterCat, setFilterCat]   = useState("");
  const [contactSearch, setContactSearch] = useState("");
  const [page, setPage]         = useState(1);
  const [totalContacts, setTotalContacts] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const PAGE_SIZE = 10;

  // Search
  const [searchMode, setSearchMode] = useState("traditional");
  const [searchQ, setSearchQ]       = useState("");
  const [aiQ, setAiQ]               = useState("");
  const [vectorQ, setVectorQ]       = useState("");
  const [results, setResults]       = useState([]);
  const [searching, setSearching]   = useState(false);
  const [searched, setSearched]     = useState(false);

  const notify = (msg, type="success") => {
    setAlert({ msg, type });
    setTimeout(() => setAlert({ msg:"", type:"" }), 4000);
  };

  const f = k => e => setForm(v => ({ ...v, [k]: e.target.value }));

  const handleDrop = useCallback(e => {
    e.preventDefault(); setDrag(false);
    const fl = e.dataTransfer.files[0]; if (fl) setFile(fl);
  }, []);

  const handleBackDrop = useCallback(e => {
    e.preventDefault(); setBackDrag(false);
    const fl = e.dataTransfer.files[0]; if (fl) setBackFile(fl);
  }, []);

  const handleExtract = async () => {
    setExtracting(true);
    try {
      const fd = new FormData();
      fd.append("front", file);
      if (backFile) fd.append("back", backFile);
      const res = await api.post("/bizcard/extract", fd);
      setForm(v => ({ ...v, ...res.data }));
      notify("Card extracted — review the details below.");
      // If this company was already researched from an earlier card, reuse
      // that instantly -- no AI call, so it's safe to run automatically.
      if (res.data.company) {
        try {
          const cached = await api.post("/bizcard/research", {
            company: res.data.company, website: res.data.website, email: res.data.email, only_cached: true,
          });
          if (cached.data.company_summary) {
            setIntel(cached.data);
            notify("Found existing research for this company — reused automatically.");
          }
        } catch { /* best-effort -- the explicit Research button still works */ }
      }
    } catch { notify("Extraction failed. Please try again.", "error"); }
    finally { setExtracting(false); }
  };

  const handleResearch = async () => {
    setResearching(true);
    try {
      const res = await api.post("/bizcard/research", { company: form.company, website: form.website, email: form.email });
      setIntel(res.data);
      if (res.data.reused) notify("Found existing research for this company — reused automatically.");
      else if (res.data.company_summary) notify("Company intelligence generated.");
      else notify("Couldn't research this company — add a website or email so we have a domain to look up.", "error");
    } catch { notify("Research failed.", "error"); }
    finally { setResearching(false); }
  };

  const handleSave = async (allowDuplicate=false) => {
    if (!form.name.trim()) { notify("Name is required.", "error"); return; }
    setSaving(true);
    try {
      await api.post("/bizcard/contacts", { ...form, ...(intel || {}), allow_duplicate: allowDuplicate });
      notify("Contact saved successfully.");
      setForm({ ...blank }); setFile(null); setBackFile(null); setIntel(null);
      // Switch to All Contacts and reload
      setTab(1);
      setFilterType(""); setFilterCat(""); setContactSearch("");
      loadContacts("", "", 1, "");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail?.existing) {
        setSaving(false);
        setDupConfirm(detail.existing);
        return;
      }
      notify(`Save failed: ${detail?.message || detail || err?.message || "Unknown error"}`, "error");
    }
    finally { setSaving(false); }
  };

  const handleCancel = () => {
    setForm({ ...blank }); setFile(null); setBackFile(null); setIntel(null);
    notify("Upload cancelled.");
  };

  const loadContacts = async (type=filterType, cat=filterCat, pageArg=1, searchArg=contactSearch) => {
    setLoading(true);
    const params = new URLSearchParams();
    if (type) params.append("contact_type", type);
    if (cat)  params.append("category", cat);
    if (searchArg) params.append("search", searchArg);
    params.append("page", pageArg);
    params.append("page_size", PAGE_SIZE);
    const res = await api.get(`/bizcard/contacts?${params}`);
    setContacts(res.data.items); setTotalContacts(res.data.total); setTotalPages(res.data.total_pages);
    setPage(res.data.page); setLoaded(true); setLoading(false);
  };

  const goToPage = (p) => {
    if (p < 1 || p > totalPages) return;
    loadContacts(filterType, filterCat, p, contactSearch);
  };

  const handleTraditionalSearch = async () => {
    setSearching(true);
    const res = await api.get("/bizcard/contacts");
    const q = searchQ.toLowerCase();
    setResults(q
      ? res.data.filter(c =>
          (c.name||"").toLowerCase().includes(q) ||
          (c.company||"").toLowerCase().includes(q) ||
          (c.email||"").toLowerCase().includes(q))
      : res.data);
    setSearched(true); setSearching(false);
  };

  const handleAISearch = async () => {
    if (!aiQ.trim()) return;
    setSearching(true);
    try {
      const res = await api.post("/bizcard/search/ai", { query: aiQ });
      setResults(res.data); setSearched(true);
    } catch { notify("AI search failed.", "error"); }
    finally { setSearching(false); }
  };

  const handleVectorSearch = async () => {
    if (!vectorQ.trim()) return;
    setSearching(true);
    try {
      const res = await api.post("/bizcard/search/vector", { query: vectorQ });
      setResults(res.data); setSearched(true);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Vector search failed.";
      notify(msg, "error");
    }
    finally { setSearching(false); }
  };

  const switchTab = (i) => {
    setTab(i); setAlert({ msg:"", type:"" });
    if (i === 1 && !loaded) loadContacts();
    if (i === 2) { setSearched(false); setResults([]); setSearchQ(""); setAiQ(""); setVectorQ(""); }
  };

  return (
    <>
      <style>{CSS}</style>

      {/* Tabs */}
      <div className="bc-tabs">
        {["📷 Add Contact","📋 All Contacts","🔍 Search"].map((label, i) => (
          <button key={i} className={`bc-tab${tab===i?" active":""}`} onClick={() => switchTab(i)}>{label}</button>
        ))}
      </div>

      {/* Alert */}
      {alert.msg && (
        <div className={`bc-alert bc-alert-${alert.type}`}>
          {alert.type === "success" ? "✓" : "⚠"} {alert.msg}
        </div>
      )}

      {/* ── TAB 0: ADD CONTACT ── */}
      {tab === 0 && (
        <div>
          {/* Single camera modal -- captures into whichever slot is still
              empty (front first, then back), so one button covers both. */}
          {showCamera && (
            <CameraModal
              onCapture={f => { if (!file) setFile(f); else setBackFile(f); setShowCamera(false); }}
              onClose={() => setShowCamera(false)}
            />
          )}
          {dupConfirm && (
            <DuplicateConfirmModal
              existing={dupConfirm}
              onCancel={() => setDupConfirm(null)}
              onConfirm={() => { setDupConfirm(null); handleSave(true); }}
            />
          )}

          {/* Drop zones, side by side like an ID/passport upload pair so both
              sides of the flip read as one unit. Neither side is actually
              required -- uploading just auto-fills the fields below via AI;
              a contact can always be added by typing the fields manually
              and skipping upload entirely (see the note under the zones). */}
          <div className="bc-dropzone-pair">
            <div className="bc-dropzone-col">
              <div className="bc-dropzone-col-label">Front <span className="bc-dropzone-opt">for AI extraction</span></div>
              <div
                className={`bc-dropzone bc-dropzone-compact${drag?" drag":""}`}
                onDragOver={e=>{e.preventDefault();setDrag(true);}}
                onDragLeave={()=>setDrag(false)}
                onDrop={handleDrop}
                onClick={()=>document.getElementById("bc-file-input").click()}>
                <div className="bc-dropzone-icon">📇</div>
                {file
                  ? <div className="bc-dropzone-file">✓ {file.name} <span onClick={e=>{e.stopPropagation();setFile(null);}} style={{marginLeft:6,cursor:"pointer"}}>✕</span></div>
                  : <div className="bc-dropzone-label">Drop image, or <span>browse</span></div>}
                <input id="bc-file-input" type="file" accept="image/*" hidden onChange={e=>setFile(e.target.files[0])} />
              </div>
            </div>

            <div className="bc-dropzone-col">
              <div className="bc-dropzone-col-label">Back <span className="bc-dropzone-opt">if details are split</span></div>
              <div
                className={`bc-dropzone bc-dropzone-compact${backDrag?" drag":""}`}
                onDragOver={e=>{e.preventDefault();setBackDrag(true);}}
                onDragLeave={()=>setBackDrag(false)}
                onDrop={handleBackDrop}
                onClick={()=>document.getElementById("bc-file-input-back").click()}>
                <div className="bc-dropzone-icon">📇</div>
                {backFile
                  ? <div className="bc-dropzone-file">✓ {backFile.name} <span onClick={e=>{e.stopPropagation();setBackFile(null);}} style={{marginLeft:6,cursor:"pointer"}}>✕</span></div>
                  : <div className="bc-dropzone-label">Only if details are on the back</div>}
                <input id="bc-file-input-back" type="file" accept="image/*" hidden onChange={e=>setBackFile(e.target.files[0])} />
              </div>
            </div>
          </div>
          <div style={{fontSize:12, color:"var(--muted)", marginTop:8}}>No card to upload? Skip this and fill in the details manually below.</div>

          <div className="bc-actions" style={{marginTop:14, marginBottom:28}}>
            <button className="bc-btn bc-btn-secondary" onClick={() => setShowCamera(true)}>
              📸 Use Camera
            </button>
            <button className="bc-btn bc-btn-primary" onClick={handleExtract} disabled={!file||extracting}>
              {extracting ? <><span className="bc-spinner"/>Extracting…</> : "🔍 Extract with AI"}
            </button>
          </div>

          {/* Contact form */}
          <div className="bc-card">
            <div className="bc-card-title">Contact Details</div>
            <div className="bc-grid">
              {[["name","Full Name *"],["designation","Designation"],["company","Company"],["email","Email"],
                ["mobile","Mobile"],["telephone","Telephone"],["website","Website"]].map(([k,label]) => (
                <div className="bc-field" key={k}>
                  <label className="bc-label">{label}</label>
                  <input className="bc-input" value={form[k]||""} onChange={f(k)} placeholder={label.replace(" *","")} />
                </div>
              ))}
              <div className="bc-field bc-grid-full">
                <label className="bc-label">Address</label>
                <textarea className="bc-textarea" value={form.address||""} onChange={f("address")} placeholder="Street, City, Country" />
              </div>
            </div>
          </div>

          <div className="bc-card">
            <div className="bc-card-title">Classification</div>
            <div className="bc-grid">
              <div className="bc-field">
                <label className="bc-label">Contact Type</label>
                <select className="bc-select" value={form.contact_type||TYPES[0]} onChange={f("contact_type")}>
                  {TYPES.map(o=><option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div className="bc-field">
                <label className="bc-label">Business Category</label>
                <CategorySelect value={form.category} onChange={v => setForm(x => ({ ...x, category: v }))} />
              </div>
            </div>
          </div>

          {/* Intelligence result */}
          {intel?.company_summary && (
            <div className="bc-intel">
              <div className="bc-intel-title">🧠 Company Intelligence</div>
              <div className="bc-intel-text" style={{whiteSpace:'pre-line'}}>{intel.company_summary}</div>
              {(intel.ai_tags||[]).length > 0 && (
                <div className="bc-tags" style={{marginBottom:10}}>{intel.ai_tags.map(t=><span key={t} className="bc-tag">{t}</span>)}</div>
              )}
              {(intel.keywords||[]).length > 0 && (
                <div className="bc-tags">{intel.keywords.map(k=><span key={k} className="bc-tag" style={{background:'rgba(16,185,129,.08)',borderColor:'rgba(16,185,129,.2)',color:'var(--green)'}}>{k}</span>)}</div>
              )}
            </div>
          )}

          <div className="bc-actions">
            <button className="bc-btn bc-btn-secondary" onClick={handleResearch}
                    disabled={researching||!form.company}>
              {researching ? <><span className="bc-spinner"/>Researching…</> : "🔬 Research Company"}
            </button>
            <button className="bc-btn bc-btn-success" onClick={()=>handleSave()} disabled={saving||!form.name.trim()}>
              {saving ? <><span className="bc-spinner"/>Saving…</> : "💾 Save Contact"}
            </button>
            <button className="bc-btn bc-btn-secondary" onClick={handleCancel} disabled={saving||researching}>
              ✕ Cancel
            </button>
            {!form.company && (
              <span style={{fontSize:12,color:'var(--muted)'}}>Fill company name to enable research</span>
            )}
          </div>
        </div>
      )}

      {/* ── TAB 1: ALL CONTACTS ── */}
      {tab === 1 && (
        <div>
          <div className="bc-filter-row">
            <div className="bc-field" style={{flex:1,minWidth:180}}>
              <label className="bc-label">Search</label>
              <input className="bc-input" placeholder="Name, company or email…" value={contactSearch}
                     onChange={e=>setContactSearch(e.target.value)}
                     onKeyDown={e=>{ if (e.key==="Enter") loadContacts(filterType, filterCat, 1, contactSearch); }} />
            </div>
            <div className="bc-field">
              <label className="bc-label">Contact Type</label>
              <select className="bc-select" value={filterType} onChange={e=>setFilterType(e.target.value)}>
                <option value="">All Types</option>
                {TYPES.map(t=><option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="bc-field">
              <label className="bc-label">Category</label>
              <select className="bc-select" value={filterCat} onChange={e=>setFilterCat(e.target.value)}>
                <option value="">All Categories</option>
                {cats.map(c=><option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <button className="bc-btn bc-btn-primary" onClick={()=>loadContacts(filterType, filterCat, 1, contactSearch)} disabled={loading}>
              {loading ? <><span className="bc-spinner"/>Loading…</> : "Load Contacts"}
            </button>
          </div>

          {loaded && (
            <div style={{fontSize:11,color:'var(--muted)',marginBottom:16,fontVariantNumeric:'tabular-nums'}}>
              {totalContacts} contact{totalContacts!==1?"s":""} found
            </div>
          )}

          {loaded && contacts.length === 0 && (
            <div className="bc-empty">
              <div className="bc-empty-icon">📭</div>
              No contacts found. Try adjusting the filters.
            </div>
          )}

          {contacts.map(c => (
            <ContactCard key={c.id} c={c}
              onUpdate={u => setContacts(prev => prev.map(x => x.id===u.id?u:x))}
              onDelete={async id => {
                await api.delete(`/bizcard/contacts/${id}`);
                setContacts(prev => prev.filter(x => x.id!==id));
                setTotalContacts(prev => Math.max(0, prev - 1));
                notify("Contact deleted.");
              }} />
          ))}

          {loaded && totalPages > 1 && (
            <div className="bc-pagination">
              <button className="bc-btn bc-btn-secondary" onClick={()=>goToPage(page-1)} disabled={page<=1||loading}>← Prev</button>
              <span style={{fontSize:12,color:'var(--muted)'}}>Page {page} of {totalPages}</span>
              <button className="bc-btn bc-btn-secondary" onClick={()=>goToPage(page+1)} disabled={page>=totalPages||loading}>Next →</button>
            </div>
          )}
        </div>
      )}

      {/* ── TAB 2: SEARCH ── */}
      {tab === 2 && (
        <div>
          <div className="bc-mode-row">
            <button className={`bc-mode-btn${searchMode==="traditional"?" active":""}`}
                    onClick={()=>{setSearchMode("traditional");setSearched(false);setResults([]);}}>
              🔎 Traditional
            </button>
            <button className={`bc-mode-btn${searchMode==="ai"?" active":""}`}
                    onClick={()=>{setSearchMode("ai");setSearched(false);setResults([]);}}>
              🤖 AI Natural Language
            </button>
            <button className={`bc-mode-btn${searchMode==="vector"?" active":""}`}
                    onClick={()=>{setSearchMode("vector");setSearched(false);setResults([]);}}
                    style={searchMode==="vector"?{background:'#b45309',borderColor:'#b45309',color:'#000'}:{}}>
              ⚡ Vector Semantic
            </button>
          </div>

          {searchMode === "traditional" && (
            <div className="bc-search-bar">
              <div className="bc-search-input-wrap">
                <span className="bc-search-icon">🔍</span>
                <input className="bc-input" placeholder="Search by name, company, or email…"
                       value={searchQ} onChange={e=>setSearchQ(e.target.value)}
                       onKeyDown={e=>e.key==="Enter"&&handleTraditionalSearch()} />
              </div>
              <button className="bc-btn bc-btn-primary" onClick={handleTraditionalSearch} disabled={searching}>
                {searching?<><span className="bc-spinner"/>Searching…</>:"Search"}
              </button>
            </div>
          )}

          {searchMode === "ai" && (
            <>
              <div style={{fontSize:12,color:'var(--text2)',marginBottom:12,lineHeight:1.6}}>
                💡 Try: <em>"Find suppliers dealing in CCTV cameras"</em> or <em>"Show cybersecurity companies"</em>
              </div>
              <div className="bc-search-bar">
                <div className="bc-search-input-wrap">
                  <span className="bc-search-icon">🤖</span>
                  <input className="bc-input" placeholder="Ask anything about your contacts…"
                         value={aiQ} onChange={e=>setAiQ(e.target.value)}
                         onKeyDown={e=>e.key==="Enter"&&handleAISearch()} />
                </div>
                <button className="bc-btn bc-btn-primary" onClick={handleAISearch} disabled={searching||!aiQ.trim()}
                        style={{background:'#7c3aed',borderColor:'#7c3aed'}}>
                  {searching?<><span className="bc-spinner"/>Searching…</>:"AI Search"}
                </button>
              </div>
            </>
          )}

          {searchMode === "vector" && (
            <>
              <div style={{fontSize:12,color:'var(--text2)',marginBottom:12,lineHeight:1.6}}>
                ⚡ Finds contacts by <strong style={{color:'#b45309'}}>meaning</strong>, not keywords. Try: <em>"cloud infrastructure"</em> or <em>"security cameras"</em>
              </div>
              <div className="bc-search-bar">
                <div className="bc-search-input-wrap">
                  <span className="bc-search-icon">⚡</span>
                  <input className="bc-input" placeholder="Search by meaning…"
                         value={vectorQ} onChange={e=>setVectorQ(e.target.value)}
                         onKeyDown={e=>e.key==="Enter"&&handleVectorSearch()} />
                </div>
                <button className="bc-btn bc-btn-primary" onClick={handleVectorSearch} disabled={searching||!vectorQ.trim()}
                        style={{background:'#b45309',borderColor:'#b45309',color:'#000'}}>
                  {searching?<><span className="bc-spinner" style={{borderTopColor:'#000'}}/>Searching…</>:"Semantic Search"}
                </button>
              </div>
            </>
          )}

          {searched && (
            <div style={{marginTop:20}}>
              <div style={{fontSize:11,color:'var(--muted)',marginBottom:14,fontVariantNumeric:'tabular-nums'}}>
                {results.length} result{results.length!==1?"s":""}
              </div>
              {results.length === 0
                ? <div className="bc-empty"><div className="bc-empty-icon">🔍</div>No matching contacts found.</div>
                : results.map(c => (
                    <ContactCard key={c.id} c={c}
                      onUpdate={u=>setResults(prev=>prev.map(x=>x.id===u.id?u:x))}
                      onDelete={async id=>{await api.delete(`/bizcard/contacts/${id}`);setResults(prev=>prev.filter(x=>x.id!==id));}} />
                  ))
              }
            </div>
          )}
        </div>
      )}
    </>
  );
}
